import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/reveal";

const FOUNDER_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_boutique-recruit/artifacts/1wll7pva_Saritha%20NB.png";

const founderLabels = ["Founder-led", "Technically grounded", "Cybersecurity", "Cloud", "Infrastructure"];

const background = [
  { num: "01", title: "Engineering Background", desc: "Technical curiosity and understanding." },
  { num: "02", title: "MBA in Human Resources", desc: "Understanding people, organisations and hiring." },
  { num: "03", title: "Technical Recruitment", desc: "Experience supporting specialist hiring across technology environments." },
];

const values = [
  { num: "01", title: "Depth Before Speed", desc: "We take the time to understand the challenge before starting the search." },
  { num: "02", title: "Quality Over Quantity", desc: "A carefully selected shortlist is more valuable than a large collection of CVs." },
  { num: "03", title: "Transparency Always", desc: "Clear expectations, honest conversations and direct feedback lead to better outcomes." },
  { num: "04", title: "Relationships That Last", desc: "We believe the best recruitment partnerships are built for the long term." },
];

const About = () => (
  <main data-testid="about-page">
    {/* 1 — HERO (light) */}
    <section className="bg-[#FAF9F5] border-b border-[#E6E2D8]">
      <div className="container-nb pt-36 pb-20 md:pt-44 md:pb-24">
        <Reveal>
          <p className="eyebrow mb-6">About NicheBytes</p>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.12] text-[#0F172A] max-w-3xl">
            Recruitment with a <span className="text-[#FF5E1A]">technical mindset.</span>
          </h1>
          <p className="mt-7 text-base md:text-lg text-[#475569] leading-relaxed max-w-xl">
            NicheBytes is a founder-led boutique recruitment firm built around technical understanding,
            focused search and meaningful relationships.
          </p>
        </Reveal>
      </div>
    </section>

    {/* 2 — MEET THE FOUNDER (white) */}
    <section className="bg-white">
      <div className="container-nb py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative max-w-md" data-testid="about-founder-saritha-jain">
              <div className="absolute -inset-3 rounded-[2rem] border border-[#E6E2D8]" />
              <div className="relative rounded-[1.75rem] overflow-hidden aspect-[4/5] bg-white">
                <img
                  src={FOUNDER_IMG}
                  alt="Saritha Jain, Founder & Technical Talent Partner at NicheBytes"
                  className="w-full h-full object-cover"
                  style={{ transform: "scale(1.08)", transformOrigin: "50% 42%" }}
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="eyebrow mb-5">Meet the Founder</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A]">
              Saritha Jain
            </h2>
            <p className="text-sm font-medium text-[#FF5E1A] mt-1.5">Founder &amp; Technical Talent Partner</p>
            <div className="mt-7 space-y-5 text-base text-[#475569] leading-relaxed max-w-2xl">
              <p>
                I'm Saritha, founder of NicheBytes. I work at the intersection of technology and hiring,
                helping companies find specialist talent across Cybersecurity, Cloud and Infrastructure —
                roles that rarely get filled through job ads alone.
              </p>
              <p>
                Most specialist searches stall because they're run like generic recruitment: keyword
                matching, wide nets, high volume. I built NicheBytes to do the opposite — a founder-led,
                technically grounded search that goes straight to the right people instead of sifting
                through everyone.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2" data-testid="founder-labels">
              {founderLabels.map((l, i) => (
                <span key={l} className="inline-flex items-center gap-5 text-xs font-semibold tracking-wide text-[#475569]">
                  {i > 0 && <span className="w-1 h-1 rounded-full bg-[#FF5E1A]" />}
                  {l}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* 3 — FOUNDER QUOTE (dark, editorial) */}
    <section className="relative bg-[#0B132B] overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 50% 60% at 20% 10%, rgba(255,94,26,0.10), transparent 70%)" }} />
      <div className="container-nb relative py-24 md:py-36">
        <Reveal className="max-w-4xl mx-auto text-center">
          <span className="font-display text-7xl md:text-8xl leading-none text-[#FF5E1A] select-none" aria-hidden="true">&ldquo;</span>
          <blockquote data-testid="founder-quote" className="font-display text-xl sm:text-2xl md:text-[2rem] font-medium tracking-tight leading-relaxed md:leading-relaxed text-[#F8FAFC] -mt-6 md:-mt-8">
            I've always been fascinated by two things: technology and people. My engineering background
            helped me understand how technical teams think, while my MBA in HR helped me understand what
            motivates people and businesses. Bringing these two worlds together is what inspired me to
            build NicheBytes.
          </blockquote>
          <div className="mt-10 flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-[#FF5E1A]" />
            <p className="text-sm font-semibold tracking-wide text-[#94A3B8]">
              Saritha Jain, <span className="text-[#FF5E1A]">Founder</span>
            </p>
            <span className="h-px w-10 bg-[#FF5E1A]" />
          </div>
        </Reveal>
      </div>
    </section>

    {/* 4 — BACKGROUND (light, minimal blocks) */}
    <section className="bg-[#FAF9F5]">
      <div className="container-nb py-20 md:py-28">
        <Reveal>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A]">Background.</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10 max-w-5xl">
          {background.map((b, i) => (
            <Reveal key={b.num} delay={i * 0.08}>
              <div className="border-t-2 border-[#0F172A] pt-6" data-testid={`background-card-${b.num}`}>
                <p className="font-mono-accent text-xs font-semibold text-[#FF5E1A] mb-3">{b.num}</p>
                <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-2">{b.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 5 — MISSION & VISION (white, editorial) */}
    <section className="bg-white border-y border-[#E6E2D8]">
      <div className="container-nb py-20 md:py-28">
        <Reveal>
          <p className="eyebrow mb-14">What We're Building</p>
        </Reveal>
        <div className="max-w-5xl space-y-16 md:space-y-20">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 items-baseline" data-testid="mission-block">
              <p className="md:col-span-3 font-mono-accent text-xs font-semibold tracking-[0.22em] uppercase text-[#FF5E1A]">Mission</p>
              <div className="md:col-span-9">
                <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A] leading-snug max-w-xl">
                  Exceptional teams, built transparently
                </h3>
                <p className="mt-4 text-base text-[#475569] leading-relaxed max-w-2xl">
                  To help innovative companies build exceptional technical teams through transparent,
                  knowledgeable and relationship-driven recruitment.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 items-baseline border-t border-[#E6E2D8] pt-16 md:pt-20" data-testid="vision-block">
              <p className="md:col-span-3 font-mono-accent text-xs font-semibold tracking-[0.22em] uppercase text-[#FF5E1A]">Vision</p>
              <div className="md:col-span-9">
                <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A] leading-snug max-w-xl">
                  Europe's most trusted boutique partner
                </h3>
                <p className="mt-4 text-base text-[#475569] leading-relaxed max-w-2xl">
                  To become one of Europe's most trusted boutique technical recruitment partners — known
                  for depth, honesty and long-term relationships.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* 6 — CORE VALUES (light) */}
    <section className="bg-[#FAF9F5]">
      <div className="container-nb py-20 md:py-28">
        <Reveal>
          <p className="eyebrow mb-5">Core Values</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A]">How we work.</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10 max-w-4xl">
          {values.map((v, i) => (
            <Reveal key={v.num} delay={i * 0.06}>
              <div className="border-t border-[#D8D2C4] pt-6" data-testid={`value-${v.num}`}>
                <p className="font-mono-accent text-xs font-semibold text-[#FF5E1A] mb-3">{v.num}</p>
                <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-2">{v.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 7 — CLOSING (dark + orange gradient) */}
    <section className="relative bg-[#0F172A] overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 65% at 50% 115%, rgba(255,94,26,0.18), transparent 70%)" }} />
      <div className="container-nb relative py-24 md:py-32 text-center">
        <Reveal>
          <p className="eyebrow mb-6">The NicheBytes Approach</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[#F8FAFC] max-w-2xl mx-auto leading-snug">
            Better understanding. Better conversations. Better hiring.
          </h2>
          <p className="mt-6 text-base text-[#94A3B8] leading-relaxed max-w-xl mx-auto">
            NicheBytes offers a more focused alternative to traditional recruitment — combining technical
            curiosity, targeted search and a genuinely personal approach.
          </p>
          <Link to="/contact" data-testid="about-cta-lets-talk" className="btn-primary mt-10 inline-flex">
            Let's Talk <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default About;
